import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WidgetValidator } from '@pt/pt-models';
import { ValidatorGenerator } from '../../documents';

@Component({
  selector: 'pt-input-text-conditional-checkbox',
  templateUrl: './input-text-conditional-checkbox.component.html',
  styles: [
    `
      .form-check-input {
        margin-top: 0.25rem;
      }

      .form-check-label {
        user-select: none;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputTextConditionalCheckboxComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputTextConditionalCheckboxComponent),
      multi: true,
    },
  ],
})
export class InputTextConditionalCheckboxComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() fontWeightBold: boolean;
  @Input() placeholder: string;
  @Input() checkboxLabel: string;
  @Input() help: string;
  @Input() error: string; // Da implementare
  @Input() enableOnCheck: boolean;
  @Input() defaultCheckValue: boolean;
  @Input() cleanOnDisable: boolean;
  @Input() activeValidatorsOnlyWhenEnabledInput: boolean;

  public autoGenerate = new FormControl(true);
  public value = new FormControl();

  public formGroup: FormGroup = new FormGroup({
    autoGenerate: this.autoGenerate,
    value: this.value,
  });

  isChanged = false;
  isRequired: boolean;
  inputId: string;
  // checkBoxValue: boolean;
  isDisabled: boolean;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;
  private validator: ValidatorFn[];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultCheckValue) {
      this.autoGenerate.setValue(changes.defaultCheckValue.currentValue, {emitEvent: false});
      this.alignDisabledCustom();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.inputId = 'ptWInputText_' + this.controlName;
    this.setFormGroup();

    this.formGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => {
        this.isChanged = t?.value !== null;
        this.alignDisabledCustom();

        if (this.onChanged) {
          this.onChanged(t ? t.value : t);
        }
        if (this.onTouched) {
          this.onTouched();
        }
      });
  }

  writeValue(obj: any): void {
    if (this.formGroup) {
      this.value.setValue(obj);
    }
    this.alignDisabledCustom();
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled && this.formGroup.enabled) {
      this.formGroup.disable({ emitEvent: false });
    }
    if (!isDisabled && this.formGroup.disabled) {
      this.formGroup.enable({ emitEvent: false });
    }
  }

  validate(value: FormControl) {
    return (
      this.formGroup.invalid && {
        invalid: true,
      }
    );
  }

  onCheckBoxChange() {
    const checkBoxValue = this.autoGenerate.value || false;
    if (this.enableOnCheck) {
      if (checkBoxValue) {
        this.formGroup.controls.value.enable();
        document.getElementById(this.inputId).focus();
      } else {
        this.formGroup.controls.value.disable();
      }
    } else {
      if (checkBoxValue) {
        this.formGroup.controls.value.disable();
      } else {
        this.formGroup.controls.value.enable();
        document.getElementById(this.inputId).focus();
      }
    }

    if (this.formGroup.controls.value.disabled && this.cleanOnDisable) {
      this.formGroup.controls.value.setValue(null);
    }

    this.manageValidators();
  }

  manageValidators() {
    if (this.formGroup) {
      if (this.dontUseValidatorsPostInit()) {
        this.cleanValidators();
      } else {
        this.setValidatorsFromInputs();
      }
    } else {
      if (this.dontUseValidatorsPreInit()) {
        this.cleanValidators();
      } else {
        this.setValidatorsFromInputs();
      }
    }
  }

  private setFormGroup() {
    this.manageValidators();
    // this.formGroup = new FormGroup({
    //   value: new FormControl(
    //     { value: null, disabled: this.defaultCheckValue },
    //     this.validator
    //   ),
    // });
  }

  private dontUseValidatorsPreInit() {
    return (
      (this.activeValidatorsOnlyWhenEnabledInput &&
        !this.defaultCheckValue &&
        this.enableOnCheck) ||
      (this.activeValidatorsOnlyWhenEnabledInput &&
        this.defaultCheckValue &&
        !this.enableOnCheck)
    );
  }

  private dontUseValidatorsPostInit() {
    return (
      this.activeValidatorsOnlyWhenEnabledInput &&
      this.formGroup.controls.value.disabled
    );
  }

  private setValidatorsFromInputs() {
    this.isRequired = ValidatorGenerator.validatorsContainsRequired(
      this.validators
    );
    this.validator = ValidatorGenerator.generateValidator(this.validators);
    this.formGroup.controls.value.setValidators(this.validator);
    this.formGroup.controls.value.updateValueAndValidity();
  }

  private cleanValidators() {
    this.isRequired = false;
    this.validator = [];
  }

  private alignDisabledCustom() {
    if (this.autoGenerate.value) {
      this.value.disable({emitEvent: false});
    } else {
      this.value.enable({emitEvent: false});
    }
  }
}
